import { ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import MobileSubscriptionAlert from '../../common/alerts/MobileSubscriptionAlert';
import WarningAlert from '../../common/alerts/WarningAlert';
import Button from '../../common/Button';
import { MeContext } from '../../common/contexts/me.context';
import Header from '../../common/Header';
import { formatCurrency } from '../../common/helpers/format';
import Loading from '../../common/Loading';
import Pagination from '../../common/Pagination';
import { PLAN_LIMITS } from '../../constants';
import noImage from '../../images/no-image.jpg';
import useProducts from './hooks/useProducts';
import { Product } from './Product.type';

const ProductList = () => {
	const { me } = useContext(MeContext);
	const { data, isFetching, onSearch, setSkip } = useProducts();
	const [pages, setPages] = useState<number[]>([]);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [dataPages, setDataPages] = useState<any>({});
	const [alertUp, setAlertUp] = useState<boolean>(false);
	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		const currPage = location.search.split('=').pop();
		setCurrentPage(Number(currPage));
	}, [location]);

	useEffect(() => {
		if (data) {
			const temp = [];
			const totalPages = Math.ceil(data.pages?.[0].metaData?.totalRecords / 10);
			for (let i = 1; i <= totalPages; i++) {
				temp.push(i);
			}
			setPages(temp);
		}
	}, [currentPage, data, dataPages]);

	useEffect(() => {
		if (data?.pages) {
			let retrievedPage: number;
			const currentSkip = data.pages?.[0]?.metaData?.skip;
			if (currentSkip === 0) {
				retrievedPage = pages.length;
			} else {
				retrievedPage = currentSkip / 10;
			}
			setDataPages((prev: any) => {
				if (!prev[retrievedPage]) {
					return { ...prev, [retrievedPage]: data?.pages[0].data };
				} else {
					return prev;
				}
			});
		}
	}, [currentPage, data, pages]);

	useEffect(() => {
		if (!dataPages[currentPage]) {
			setSkip((currentPage - 1) * 10);
		}
	}, [currentPage, dataPages, setSkip]);

	const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		onSearch(val);
	};

	const limitReached = useMemo(() => {
		if (me.account?.newPlan) {
			if (me?.account?.subscription?.options?.unlimitedListings) {
				return false;
			} else {
				return (data?.pages?.[0]?.metaData?.allRecords || 0) >= 5;
			}
		} else {
			return (
				(data?.pages?.[0]?.metaData?.allRecords || 0) >=
				PLAN_LIMITS[me.account?.subscription?.plan].products
			);
		}
	}, [data, me]);

	useEffect(() => {
		const screen = window.screen;
		if (
			limitReached &&
			screen.width < 640 &&
			(data?.pages?.[0]?.metaData?.allRecords || 0) >= 5
		) {
			setAlertUp(true);
		}
	}, [data?.pages, limitReached]);

	const setNewPage = (val: number) => {
		navigate(`/products?page=${String(val)}`);
	};

	const clickAnalytics = me?.account?.subscription?.options?.clickAnalytics;

	return alertUp ? (
		<MobileSubscriptionAlert
			title="Uh oh!"
			subtitle="Looks like you've reached your maximum amount of listings. Would you like to update your plan?"
			acceptLink="/billing"
			acceptMessage="I'd like to update my membership to include unlimited listings."
			rejectMessage="No thanks"
			close={() => setAlertUp(false)}
			icon={<ExclamationCircleIcon className="h-12 w-12 text-black" />}
		/>
	) : (
		<>
			<Header
				title="Products"
				className="pb-6"
				rightSide={
					<>
						<Link to="/products/new">
							<Button text="Add product" disabled={limitReached} />
						</Link>
					</>
				}
			>
				<div className="w-full sm:w-1/2 pt-2">
					<label htmlFor="search" className="sr-only">
						Search
					</label>
					<div className="relative">
						<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
							<svg
								className="h-5 w-5 text-gray-400"
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
								fill="currentColor"
								aria-hidden="true"
							>
								<path
									fillRule="evenodd"
									d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
									clipRule="evenodd"
								/>
							</svg>
						</div>
						<input
							id="search"
							name="search"
							className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white shadow-sm placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
							placeholder="Search"
							type="search"
							onChange={onChangeSearch}
						/>
					</div>
				</div>
			</Header>
			<div className="p-6">
				{limitReached && (
					<WarningAlert>
						Subscription limit met. You can only have 5 products with your
						current subscription.{' '}
						<Link
							to="/billing"
							className="font-medium underline text-yellow-700 hover:text-yellow-600"
						>
							Upgrade your account to add more products.
						</Link>
					</WarningAlert>
				)}
			</div>
			<div className="mb-12">
				{isFetching ? (
					<Loading
						color="text-primary-500 w-full mx-auto"
						className="col-span-4"
					/>
				) : (
					<ul className="px-6 space-y-12 sm:grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:space-y-0 xl:grid-cols-4 2xl:grid-cols-5 w-full">
						{dataPages[currentPage]?.map(
							(row: Partial<Product> & { $new?: boolean }) => (
								<li key={row.id} className="cursor-pointer relative">
									{!!row.sold && (
										<div className="ribbon z-20">
											<span>sold</span>
										</div>
									)}
									{!!row.pinned && (
										<div className="ribbon pinned z-20">
											<span>pinned</span>
										</div>
									)}
									<Link to={`/products/${row.id}`}>
										<div className="space-y-4" data-cy="products">
											<div className="aspect-w-3 aspect-h-2 relative">
												<img
													className={[
														'object-cover shadow-lg rounded-lg',
														row.active ? '' : 'opacity-30',
													].join(' ')}
													src={row.images?.[0].url || noImage}
													alt={row.title}
												/>
											</div>

											<div className="space-y-1">
												<div className="text-lg leading-5 font-medium space-y-1">
													<h3>
														<span className={row.active ? '' : 'mr-1'}>
															{row.title}
														</span>
														{!row.active && (
															<span className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-red-500 text-white">
																<XCircleIcon className="mr-1 h-3 w-3 text-white" />
																Inactive
															</span>
														)}
													</h3>
													<span className="text-primary-600 flex gap-2">
														<p
															className={[
																row.reducedPrice ? 'line-through' : '',
															].join(' ')}
														>
															{formatCurrency(row.price)}
														</p>
														{row.reducedPrice && (
															<p>{formatCurrency(row.reducedPrice)}</p>
														)}
													</span>
												</div>
												{clickAnalytics && (
													<div className="flex space-x-5 text-gray-400 text-sm">
														{row.views} views
													</div>
												)}
											</div>
										</div>
									</Link>
								</li>
							)
						)}
					</ul>
				)}
			</div>
			<Pagination
				pages={pages}
				currentPage={currentPage}
				totalResults={data?.pages?.[0]?.metaData?.totalRecords}
				setCurrentPage={setNewPage}
			/>
		</>
	);
};

export default ProductList;
